/**
 * @description: 筛选弹出框组件
 * @author: zhouyh
 * @time: 2023/8/10 10:04
 */
import React, { useCallback, useMemo } from "react";
import styles from "./filterModal.module.scss";
import ColorContent from "@/src/components/FilterModalNew/components/ColorContent";
import SizeContent from "@/src/components/FilterModalNew/components/SizeContent";
import GenderContent from "@/src/components/FilterModalNew/components/GenderContent";
import MaterialContent from "@/src/components/FilterModalNew/components/MaterialContent";
import ShapeContent from "@/src/components/FilterModalNew/components/ShapeContent";
import RimContent from "@/src/components/FilterModalNew/components/RimContent";
import FeatureContent from "@/src/components/FilterModalNew/components/FeatureContent";
import {
    DrawerButton,
    FmAccordion,
    FmDrawer,
    ISingleAccordion,
} from "@/ui-component";
import classnames from "classnames";
import { useFilterConditionContext } from "@/src/provider";
import { useTranslation } from "@/src/i18n/client";
import { useAccordionStore } from "@/ui-component/store/accordion";
import FilterSelectNew from "../FilterSelectNew";
import { SelectedTag } from "./components/SelectedTag";

interface FilterModalProps {
    open: boolean;
    onClose: () => void;
    refresh: () => void;
}

const FilterModalNew: React.FC<FilterModalProps> = ({
    open,
    onClose,
    refresh,
}) => {
    const [
        filterNavConfig,
        isFilter,
        initFilterConditions,
        sortByData,
        sortBy,
        changeFilterConditions,
        flatFilterArr,
        deleteSelectedOption,
    ] = useFilterConditionContext((state) => [
        state.filterNavConfig,
        state.isFilter,
        state.initFilterConditions,
        state.sortByData,
        state.sortBy,
        state.changeFilterConditions,
        state.flatFilterArr,
        state.deleteSelectedOption,
    ]);
    const expanded = useAccordionStore((state) => state.expanded);
    const { t } = useTranslation("specialSub");
    const onClear = () => {
        if (!isFilter) {
            return;
        }
        initFilterConditions();
        refresh?.();
    };

    const onConfirm = () => {
        refresh?.();
        onClose?.();
    };

    const tabChildrenList = useMemo(() => {
        return [
            <ColorContent key={0} />,
            <SizeContent key={1} />,
            <GenderContent key={2} />,
            // <AgeRangeContent key={3} />,
            <MaterialContent key={4} />,
            <ShapeContent key={5} />,
            <RimContent key={6} />,
            <FeatureContent key={7} />,
        ];
    }, []);
    // 排序change事件
    const sortByOnChange = useCallback(
        (obj: any) => {
            changeFilterConditions({ sortBy: obj });
        },
        [changeFilterConditions],
    );

    const filterTabs: ISingleAccordion[] = useMemo(() => {
        return filterNavConfig.map((item, index: number) => {
            return {
                id: t(`specialSub:${item.title}`),
                title: (
                    <>
                        {t(`specialSub:${item.title}`)}
                        <p
                            className={classnames(
                                styles["title-active-number"],
                                {
                                    ["hidden"]: !item.useCount,
                                },
                            )}
                        >
                            {item.useCount}
                        </p>
                    </>
                ),
                key: index,
                details: {
                    children: tabChildrenList[index],
                },
                classes: {
                    root: styles["filter-item-title"],
                },
                expandIcon: {
                    icon: expanded == item.title ? "icon-minus" : "icon--plus",
                },
            };
        });
    }, [expanded, filterNavConfig, t, tabChildrenList]);

    return (
        <FmDrawer
            open={open}
            closeable
            closeablePosition="right"
            onClose={onClose}
            // headerChildren={t("specialSub:CustomFilter")}
            headerClassName={classnames(
                styles["header-bottom"],
                "header-bottom",
            )}
            // fullHeight
            className={classnames(
                styles["filter-nav-bar-container"],
                "filter-nav-bar-container",
            )}
            drawerStyle={{
                height: "14rem",
                maxHeight: "15.6rem",
                backgroundColor: "#f2f2f2",
                padding:'0 0.2rem'
            }}
            childrenClassName={styles["filter-nav-bar-children-container"]}
            anchor={"bottom"}
            horizontal
            footer={
                <>
                    {flatFilterArr.length > 0 && (
                        <div className={styles["selected"]}>
                            {flatFilterArr.map((item) => (
                                <SelectedTag
                                    key={item.title}
                                    value={item.title}
                                    onClick={() =>
                                        deleteSelectedOption(item, item.key)
                                    }
                                />
                            ))}
                        </div>
                    )}
                    <DrawerButton
                        buttonProps={[
                            {
                                disabled: !isFilter,
                                onClick: onClear,
                                children: t("Clear All"),
                            },
                            {
                                onClick: onConfirm,
                                children: t("specialSub:Confirm"),
                            },
                        ]}
                    />
                </>
            }
        >
            <FilterSelectNew
                dataList={sortByData}
                onChange={sortByOnChange}
                selectedItem={sortBy}
            ></FilterSelectNew>
            <FmAccordion
                accordions={filterTabs}
                accordionDetailsClassName={
                    styles["accordion-details-children-container"]
                }
                isIcon={true}
            ></FmAccordion>
        </FmDrawer>
    );
};

export default FilterModalNew;
